import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck, Lock, Globe2, Smartphone, Sparkles, ArrowRight, User as UserIcon,
} from "lucide-react";
import { clientLogin, apiErrorMsg } from "@/lib/api";

function Capability({ icon, title, desc }) {
  return (
    <div className="flex items-start gap-3 p-3 bg-white/70 backdrop-blur-sm border border-black/[0.06] rounded-lg hover:border-[#ff5722]/40 hover:bg-white transition-colors">
      <span className="shrink-0 h-10 w-10 grid place-items-center bg-white border border-black/[0.08] rounded-lg shadow-sm">{icon}</span>
      <div>
        <div className="text-sm font-bold text-[#0a0a0a]">{title}</div>
        <div className="text-xs text-[#4a4a4a] leading-snug mt-0.5">{desc}</div>
      </div>
    </div>
  );
}

export default function LoginScreen({ onLoggedIn }) {
  const [params] = useSearchParams();
  const [username, setUsername] = useState(params.get("u") || "");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) return;
    setBusy(true);
    try {
      const { token, client } = await clientLogin(username.trim().toLowerCase(), password);
      onLoggedIn(token, client);
    } catch (err) {
      toast.error(apiErrorMsg(err, "Login failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white">
      {/* LEFT: Brand hero */}
      <aside className="relative overflow-hidden bg-gradient-to-br from-[#fff7f4] via-[#fff2eb] to-white px-8 py-10 md:px-14 md:py-16 flex flex-col">
        <span className="pointer-events-none absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#ff5722]/15 blur-3xl" />
        <span className="pointer-events-none absolute -bottom-32 -left-24 w-[380px] h-[380px] rounded-full bg-[#ff5722]/12 blur-3xl" />
        <svg className="pointer-events-none absolute inset-0 w-full h-full opacity-[0.04]" aria-hidden="true">
          <defs>
            <pattern id="grid" width="48" height="48" patternUnits="userSpaceOnUse">
              <path d="M 48 0 L 0 0 0 48" fill="none" stroke="#0a0a0a" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>

        <div className="relative flex-1 flex flex-col">
          <a href="/" className="inline-flex items-center" aria-label="Reachvel">
            <img src="/reachvel-logo-light.png" alt="Reachvel" className="h-10 md:h-12 w-auto" />
          </a>

          <div className="mt-8 md:mt-10 max-w-md">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[#0a0a0a] leading-[1.05]">
              AI Engineering<br/>for What&rsquo;s Next<span className="text-[#ff5722]">.</span>
            </h1>
            <p className="mt-5 text-sm md:text-base text-[#4a4a4a] leading-relaxed max-w-sm">
              We build intelligent digital solutions across Web, Mobile, and AI Automations to help you innovate, scale, and lead with confidence.
            </p>
            <div className="mt-6 h-px w-12 bg-[#ff5722]" />
          </div>

          <div className="mt-10 grid gap-3 max-w-md">
            <Capability icon={<Globe2 className="h-5 w-5 text-[#ff5722]" />} title="Web Engineering" desc="Modern, scalable, and secure web applications built for performance." />
            <Capability icon={<Smartphone className="h-5 w-5 text-[#ff5722]" />} title="Mobile Engineering" desc="Native and cross-platform mobile experiences that users love." />
            <Capability icon={<Sparkles className="h-5 w-5 text-[#ff5722]" />} title="AI Automations" desc="Intelligent automations that streamline processes and unlock new possibilities." />
          </div>

          <a href="https://www.reachvel.com" target="_blank" rel="noopener noreferrer"
            className="mt-8 inline-flex items-center justify-between gap-4 p-4 bg-white border border-black/10 rounded-lg max-w-md hover:border-[#ff5722] hover:shadow-md transition-all group">
            <span className="inline-flex items-center gap-3">
              <Globe2 className="h-5 w-5 text-[#ff5722]" />
              <span>
                <span className="block text-[11px] font-mono uppercase tracking-[0.15em] text-[#4a4a4a]">Visit our website</span>
                <span className="block text-sm font-semibold text-[#ff5722]">www.reachvel.com</span>
              </span>
            </span>
            <ArrowRight className="h-4 w-4 text-[#0a0a0a] group-hover:text-[#ff5722] group-hover:translate-x-0.5 transition-all" />
          </a>

          <div className="mt-auto pt-10 inline-flex items-center gap-2 text-xs text-[#4a4a4a]">
            <ShieldCheck className="h-4 w-4 text-[#ff5722]" />
            <span>Secure. Reliable. Built for collaboration.</span>
          </div>
        </div>
      </aside>

      {/* RIGHT: Sign-in form */}
      <section className="px-6 py-10 md:px-14 md:py-16 flex flex-col">
        <div className="flex justify-end">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border border-black/10 rounded-lg text-[#4a4a4a]">
            <Globe2 className="h-3.5 w-3.5" /> English
          </span>
        </div>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md mx-auto">
            <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-[#ff5722] font-bold">Client Portal</div>
            <h2 className="mt-2 text-4xl md:text-5xl font-bold tracking-tight text-[#0a0a0a]">Sign in</h2>
            <p className="mt-2 text-sm text-[#4a4a4a]">Enter the credentials shared by your project manager.</p>

            <form onSubmit={submit} className="mt-10 space-y-5">
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-[0.2em] text-[#4a4a4a] mb-2">Username</label>
                <div className="relative">
                  <UserIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9a9a9a]" />
                  <input
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoFocus
                    placeholder="reachvel"
                    data-testid="client-login-username"
                    className="w-full font-mono border border-black/15 rounded-lg pl-10 pr-3 py-3 text-sm outline-none focus:border-[#ff5722] focus:ring-2 focus:ring-[#ff5722]/20" />
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-[0.2em] text-[#4a4a4a] mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9a9a9a]" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    data-testid="client-login-password"
                    className="w-full font-mono border border-black/15 rounded-lg pl-10 pr-3 py-3 text-sm outline-none focus:border-[#ff5722] focus:ring-2 focus:ring-[#ff5722]/20" />
                </div>
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-[#0a0a0a] cursor-pointer select-none">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
                  className="h-4 w-4 accent-[#ff5722]" />
                Remember me
              </label>
              <button type="submit" disabled={busy} data-testid="client-login-submit"
                className="w-full bg-[#0a0a0a] text-white font-bold uppercase tracking-[0.2em] text-sm py-4 rounded-full hover:bg-[#ff5722] transition-colors disabled:opacity-60">
                {busy ? "Signing in…" : "Sign in"}
              </button>
            </form>

            <div className="mt-8 flex items-center gap-4">
              <span className="h-px flex-1 bg-black/10" />
              <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#9a9a9a]">or</span>
              <span className="h-px flex-1 bg-black/10" />
            </div>

            <p className="mt-6 text-center text-sm text-[#4a4a4a]">
              Need access? <a href="mailto:hello@reachvel.com" className="text-[#ff5722] font-semibold hover:underline">Contact your account manager</a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
